import { API, Auth } from "aws-amplify";
import React, { useState, useEffect } from "react";
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import '../registration/RegistrationForm.css';

export default function RoadLessonViewer() {
    const [sortedLessons, setSortedLessons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [attemptedToLoadData, setAttemptedToLoadData] = useState(false);
    const [dateError, setDateError] = useState(null);
    const [instructorError, setInstructorError] = useState(null);

    useEffect(() => {
        fetchRoadTestInstructors();
        handleGetFutureLessons();
    }, []);

    async function fetchRoadTestInstructors() {
        try {
            const response = await fetch('https://s3.amazonaws.com/publicdates/roadTestInstructors.json', {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                method: 'GET'
            });
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }
            await response.json();
        } catch (error) {
            console.log(error);
            setInstructorError("Failed to load instructors. Please refresh the page.");
        }
    }

    async function handleGetFutureLessons() {
        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const options = {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': session.idToken.jwtToken
                }
            };
            const data = await API.get("APIGateway", "/getallfuturelessons", options);
            const sorted = data
                .map(lesson => ({ ...lesson, sortString: lesson.date + lesson.startTime }))
                .sort((a, b) => String(a.sortString).localeCompare(b.sortString));
            setSortedLessons(sorted);
            setAttemptedToLoadData(true);
            setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
            setAttemptedToLoadData(true);
            if (error.response) {
                if (error.response.status === 401) {
                    setDateError("Authentication Failed. Please log back in.");
                } else {
                    setDateError("There was an error saving your dates. Please copy this message and send it to your developer for debugging." + JSON.stringify(error.response));
                }
            } else {
                setDateError("Network error.");
            }
        }
    }

    function formatPhone(phone) {
        if (phone.length === 10) {
            return '(' + phone.substr(0, 3) + ') ' + phone.substr(3, 3) + '-' + phone.substr(6);
        }
        return phone;
    }

    function renderRow(slot) {
        const students = [];
        const bookedInstructors = [];

        for (const instructor in slot.bookedStudents) {
            bookedInstructors.push(instructor);
            if (slot.bookedStudents[instructor].hasOwnProperty("name")) {
                students.push({
                    date: slot.date,
                    slotName: slot.slotName,
                    name: slot.bookedStudents[instructor].name,
                    phone: formatPhone(slot.bookedStudents[instructor].phone),
                    location: slot.location,
                    instructor: instructor
                });
            }
        }

        for (const instructor in slot.selectedInstructors) {
            if (!bookedInstructors.includes(slot.selectedInstructors[instructor].label)) {
                students.push({
                    date: slot.date,
                    slotName: slot.slotName,
                    name: "",
                    phone: "",
                    location: slot.location,
                    instructor: slot.selectedInstructors[instructor].label
                });
            }
        }

        return students.map(student => (
            <tr
                key={student.date + student.slotName + student.instructor}
                className="d-flex"
                style={!student.name ? { opacity: 0.5 } : undefined}
            >
                <td className="col-2">{student.date}</td>
                <td className="col-2">{student.location}</td>
                <td className="col-2">{student.slotName}</td>
                <td className="col-2">
                    {student.name || <Badge variant="secondary">Open</Badge>}
                </td>
                <td className="col-2">{student.phone}</td>
                <td className="col-2">{student.instructor}</td>
            </tr>
        ));
    }

    return (
        <div className="holder">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                <h3 style={{ margin: 0 }}>View All Scheduled Road Test Lessons</h3>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={loading}
                    onClick={() => { setAttemptedToLoadData(false); handleGetFutureLessons(); }}
                >
                    {loading ? <><Spinner animation="border" size="sm" /> Refreshing...</> : "Refresh"}
                </Button>
            </div>

            {instructorError && <Alert variant="danger">{instructorError}</Alert>}
            {dateError && <Alert variant="danger">{dateError}</Alert>}

            {loading ? (
                <div style={{ textAlign: "center", padding: "3rem 0" }}>
                    <Spinner animation="border" role="status" />
                    <div style={{ marginTop: "0.75rem", color: "#6c757d" }}>Loading lessons...</div>
                </div>
            ) : attemptedToLoadData ? (
                sortedLessons.length > 0 ? (
                    <Table striped bordered hover responsive size="sm">
                        <thead className="thead-dark">
                            <tr className="d-flex">
                                <th className="col-2">Date</th>
                                <th className="col-2">Location</th>
                                <th className="col-2">Slot Time</th>
                                <th className="col-2">Student</th>
                                <th className="col-2">Phone</th>
                                <th className="col-2">Instructor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedLessons.map(slot => renderRow(slot))}
                        </tbody>
                    </Table>
                ) : (
                    <Card body className="text-center text-muted" style={{ padding: "2rem" }}>
                        No upcoming lessons found.
                    </Card>
                )
            ) : null}
        </div>
    );
}
